/**
 * GasEstimate - Display real-time gas price estimates
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Fuel, 
  Clock, 
  Zap, 
  ChevronDown,
  RefreshCw,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { cn } from '../utils/cn';

interface GasPrice {
  chainId: number;
  chainName: string;
  nativeSymbol: string;
  gasPrices: {
    slow: { gwei: number; usd: number; time: string };
    standard: { gwei: number; usd: number; time: string };
    fast: { gwei: number; usd: number; time: string };
  };
  estimates: {
    transfer: { gas: number; slow: number; standard: number; fast: number };
    erc20: { gas: number; slow: number; standard: number; fast: number };
    swap: { gas: number; slow: number; standard: number; fast: number };
  };
  lastUpdated: string;
}

interface GasEstimateProps {
  chainId?: number;
  txType?: 'transfer' | 'erc20' | 'swap';
  showDetails?: boolean;
  compact?: boolean;
}

export default function GasEstimate({ 
  chainId = 1, 
  txType = 'transfer',
  showDetails = false,
  compact = false,
}: GasEstimateProps) {
  const [selectedSpeed, setSelectedSpeed] = useState<'slow' | 'standard' | 'fast'>('standard');
  const [expanded, setExpanded] = useState(showDetails);

  const { data: gasPrice, isLoading, refetch } = useQuery<GasPrice>({
    queryKey: ['gas-price', chainId],
    queryFn: async () => {
      const response = await fetch(`/api/gas/price/${chainId}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    refetchInterval: 15000, // Refresh every 15 seconds
    staleTime: 10000,
  });

  if (isLoading) {
    return (
      <div className={cn(
        "animate-pulse",
        compact ? "h-8 w-24 bg-white/[0.04] rounded-lg" : "h-20 bg-white/[0.04] rounded-xl"
      )} />
    );
  }

  if (!gasPrice) {
    return (
      <div className="text-sm text-[#8b949e]">Gas data unavailable</div>
    );
  }

  const currentEstimate = gasPrice.estimates[txType][selectedSpeed];
  const currentGasPrice = gasPrice.gasPrices[selectedSpeed];

  if (compact) {
    return (
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.04] rounded-lg hover:bg-white/[0.06] transition-colors"
      >
        <Fuel className="h-4 w-4 text-[#00d4aa]" />
        <span className="text-sm text-white font-medium">
          ${currentEstimate.toFixed(2)}
        </span>
        <span className="text-xs text-[#8b949e]">
          ({currentGasPrice.gwei.toFixed(1)} gwei)
        </span>
      </button>
    );
  }

  return (
    <div className="bg-[#161b22] border border-white/[0.08] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/[0.08] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Fuel className="h-5 w-5 text-[#00d4aa]" />
          <span className="font-medium text-white">Gas Estimate</span>
          <span className="text-sm text-[#8b949e]">({gasPrice.chainName})</span>
        </div>
        <button
          onClick={() => refetch()}
          className="p-1.5 rounded-lg hover:bg-white/[0.04] transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4 text-[#8b949e]" />
        </button>
      </div>

      {/* Speed Selector */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {(['slow', 'standard', 'fast'] as const).map((speed) => {
            const priceInfo = gasPrice.gasPrices[speed];
            const estimate = gasPrice.estimates[txType][speed];
            const Icon = speed === 'slow' ? Clock : speed === 'fast' ? Zap : TrendingUp;
            
            return (
              <button
                key={speed}
                onClick={() => setSelectedSpeed(speed)}
                className={cn(
                  "p-3 rounded-xl border transition-all text-left",
                  selectedSpeed === speed
                    ? "border-[#00d4aa] bg-[#00d4aa]/10"
                    : "border-white/[0.08] hover:border-white/[0.15] bg-white/[0.02]"
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={cn(
                    "h-4 w-4",
                    selectedSpeed === speed ? "text-[#00d4aa]" : "text-[#8b949e]"
                  )} />
                  <span className={cn(
                    "text-sm font-medium capitalize",
                    selectedSpeed === speed ? "text-white" : "text-[#8b949e]"
                  )}>
                    {speed}
                  </span>
                </div>
                <div className="text-lg font-bold text-white">
                  ${estimate.toFixed(2)}
                </div>
                <div className="text-xs text-[#8b949e]">
                  {priceInfo.gwei.toFixed(1)} gwei • {priceInfo.time}
                </div>
              </button>
            );
          })}
        </div>

        {/* Transaction Type Info */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#8b949e]">Transaction type</span>
          <span className="text-white capitalize">{txType}</span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-[#8b949e]">Gas limit</span>
          <span className="text-white">{gasPrice.estimates[txType].gas.toLocaleString()}</span>
        </div>

        {/* Expandable Details */}
        {expanded && (
          <div className="pt-4 border-t border-white/[0.08] space-y-3">
            <div className="text-sm font-medium text-white mb-2">All Transaction Types</div>
            {(['transfer', 'erc20', 'swap'] as const).map((type) => (
              <div 
                key={type}
                className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg"
              >
                <div>
                  <div className="text-sm font-medium text-white capitalize">{type}</div>
                  <div className="text-xs text-[#8b949e]">
                    {gasPrice.estimates[type].gas.toLocaleString()} gas
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-white">
                    ${gasPrice.estimates[type][selectedSpeed].toFixed(2)}
                  </div>
                  <div className="text-xs text-[#8b949e]">
                    ~{gasPrice.gasPrices[selectedSpeed].time}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-center gap-1 py-2 text-sm text-[#8b949e] hover:text-white transition-colors"
        >
          <span>{expanded ? 'Hide' : 'Show'} details</span>
          <ChevronDown className={cn(
            "h-4 w-4 transition-transform",
            expanded && "rotate-180"
          )} />
        </button>

        {/* Last Updated */}
        <div className="text-center text-xs text-[#5e6673]">
          Updated {new Date(gasPrice.lastUpdated).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}

// Compact inline gas display
export function GasEstimateInline({ 
  chainId = 1, 
  txType = 'transfer' 
}: { 
  chainId?: number; 
  txType?: 'transfer' | 'erc20' | 'swap';
}) {
  const { data: gasPrice, isLoading } = useQuery<GasPrice>({
    queryKey: ['gas-price', chainId],
    queryFn: async () => {
      const response = await fetch(`/api/gas/price/${chainId}`);
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      return data.data;
    },
    refetchInterval: 15000,
    staleTime: 10000,
  });

  if (isLoading || !gasPrice) {
    return <span className="text-[#8b949e]">--</span>;
  }

  const estimate = gasPrice.estimates[txType].standard;
  const gwei = gasPrice.gasPrices.standard.gwei;

  return (
    <span className="inline-flex items-center gap-1.5">
      <Fuel className="h-3.5 w-3.5 text-[#00d4aa]" />
      <span className="text-white font-medium">${estimate.toFixed(2)}</span>
      <span className="text-[#8b949e]">({gwei.toFixed(0)} gwei)</span>
    </span>
  );
}
